import {
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
    type KeyboardEvent,
    type PointerEvent,
    type RefObject,
} from "react";

/** Where a floating window's top-left corner sits, in CSS pixels from the viewport's. */
export interface WindowPosition {
    x: number;
    y: number;
}

/** How large a floating window is drawn, in CSS pixels. */
export interface WindowSize {
    width: number;
    height: number;
}

/** The edge or corner a window is resized from, by compass point. */
export type ResizeEdge = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

/** How close to the edge of the screen a window may be moved. */
export const FLOATING_WINDOW_MARGIN = 8;

/** One arrow press moves or resizes a window this far; with Shift, four times as far. */
export const FLOATING_WINDOW_KEY_STEP = 16;

/**
 * The smallest a window may be made, in rem: the title bar, the buttons at
 * its foot and a few lines of what it holds. In rem rather than pixels,
 * because what has to fit is type, and the root grows under the
 * high-contrast skin and shrinks on a phone.
 */
export const FLOATING_WINDOW_MIN_SIZE_REM: WindowSize = { width: 18, height: 16 };

/** A press that travels less than this is a click, not a drag. */
const DRAG_THRESHOLD = 3;

/**
 * The place nearest `position` at which the whole window is on the screen,
 * `margin` in from its edges. Whole, not just its title bar, as an operating
 * system would allow: a window whose Send button has slid off the bottom is a
 * window that cannot be finished without first being found again. One larger
 * than the screen is pinned to its top-left corner, so its title bar stays in
 * reach.
 */
export function clampWindowPosition(
    position: WindowPosition,
    size: WindowSize,
    viewport: WindowSize,
    margin = FLOATING_WINDOW_MARGIN,
): WindowPosition {
    const maxX = Math.max(margin, viewport.width - size.width - margin);
    const maxY = Math.max(margin, viewport.height - size.height - margin);
    return {
        x: Math.round(Math.min(Math.max(position.x, margin), maxX)),
        y: Math.round(Math.min(Math.max(position.y, margin), maxY)),
    };
}

/**
 * Keep `value` within `[min, max]`, except that `max` — what the screen has
 * room for — outranks `min`: a window that cannot be seen whole cannot be
 * finished, however small that makes it.
 */
function clampLength(value: number, min: number, max: number): number {
    return Math.max(Math.min(value, max), Math.min(min, max));
}

/**
 * The size nearest `size` at which the window fits on the screen, `margin` in
 * from its edges, and is no smaller than `min` — unless the screen itself is.
 */
export function clampWindowSize(
    size: WindowSize,
    viewport: WindowSize,
    min: WindowSize,
    margin = FLOATING_WINDOW_MARGIN,
): WindowSize {
    return {
        width: Math.round(clampLength(size.width, min.width, viewport.width - 2 * margin)),
        height: Math.round(clampLength(size.height, min.height, viewport.height - 2 * margin)),
    };
}

function isWindowPosition(value: unknown): value is WindowPosition {
    return typeof value === "object" && value !== null
        && Number.isFinite((value as WindowPosition).x)
        && Number.isFinite((value as WindowPosition).y);
}

function isWindowSize(value: unknown): value is WindowSize {
    if (typeof value !== "object" || value === null) return false;
    const { width, height } = value as Partial<WindowSize>;
    return typeof width === "number" && typeof height === "number"
        && Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;
}

/** What was stored under `storageKey`, read as JSON; null when nothing usable is. */
function readStoredWindow(storageKey: string): unknown {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(storageKey);
        return raw ? (JSON.parse(raw) as unknown) : null;
    } catch {
        return null;
    }
}

/** Where the operator last put a window; null when they never moved it, or it cannot be read. */
export function readStoredWindowPosition(storageKey: string): WindowPosition | null {
    const stored = readStoredWindow(storageKey);
    return isWindowPosition(stored) ? { x: stored.x, y: stored.y } : null;
}

/** How large the operator last made a window; null when they never resized it, or it cannot be read. */
export function readStoredWindowSize(storageKey: string): WindowSize | null {
    const stored = readStoredWindow(storageKey);
    return isWindowPosition(stored) && isWindowSize(stored) ? { width: stored.width, height: stored.height } : null;
}

/**
 * Remember where the operator put a window, and how large they made it when
 * they resized it; a null place forgets both. One record, so the place and the
 * size it was chosen at are never read back from two different visits.
 */
export function writeStoredWindowPosition(
    storageKey: string,
    position: WindowPosition | null,
    size: WindowSize | null = null,
): void {
    if (typeof window === "undefined") return;
    try {
        if (!position) {
            window.localStorage.removeItem(storageKey);
            return;
        }
        const record = size
            ? { x: position.x, y: position.y, width: size.width, height: size.height }
            : { x: position.x, y: position.y };
        window.localStorage.setItem(storageKey, JSON.stringify(record));
    } catch {
        // Non-critical: the window still moves, it just opens in its corner next time.
    }
}

/** The screen a window has to stay on: the layout viewport, without a document scrollbar. */
function viewportSize(): WindowSize {
    const root = document.documentElement;
    return {
        width: root.clientWidth || window.innerWidth,
        height: root.clientHeight || window.innerHeight,
    };
}

/** The smallest a window may be, in pixels at the root's type size as it is now. */
function minimumSize(): WindowSize {
    const rootSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    const rem = Number.isFinite(rootSize) && rootSize > 0 ? rootSize : 16;
    return {
        width: FLOATING_WINDOW_MIN_SIZE_REM.width * rem,
        height: FLOATING_WINDOW_MIN_SIZE_REM.height * rem,
    };
}

/** A window's place and size as one box. */
interface WindowBox extends WindowPosition, WindowSize {}

/**
 * The box a resize from `edge` leaves the window in, the pointer having moved
 * (dx, dy) since the press: the edges not being dragged stay where they were,
 * the dragged ones stop at the minimum size and at the edge of the screen.
 */
function resizedBox(origin: WindowBox, edge: ResizeEdge, dx: number, dy: number, viewport: WindowSize, min: WindowSize): WindowBox {
    const margin = FLOATING_WINDOW_MARGIN;
    const box = { ...origin };
    if (edge.includes("e")) {
        box.width = clampLength(origin.width + dx, min.width, viewport.width - margin - origin.x);
    } else if (edge.includes("w")) {
        const right = origin.x + origin.width;
        box.width = clampLength(origin.width - dx, min.width, right - margin);
        box.x = right - box.width;
    }
    if (edge.includes("s")) {
        box.height = clampLength(origin.height + dy, min.height, viewport.height - margin - origin.y);
    } else if (edge.includes("n")) {
        const bottom = origin.y + origin.height;
        box.height = clampLength(origin.height - dy, min.height, bottom - margin);
        box.y = bottom - box.height;
    }
    return {
        x: Math.round(box.x),
        y: Math.round(box.y),
        width: Math.round(box.width),
        height: Math.round(box.height),
    };
}

interface DragState {
    pointerId: number;
    startX: number;
    startY: number;
    origin: WindowPosition;
    size: WindowSize;
    at: WindowPosition;
    moved: boolean;
}

interface ResizeState {
    pointerId: number;
    edge: ResizeEdge;
    startX: number;
    startY: number;
    origin: WindowBox;
    at: WindowBox;
    moved: boolean;
}

/** The pointer handlers a draggable part of the window spreads on itself. */
export interface PointerHandleProps {
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
    onLostPointerCapture: (event: PointerEvent<HTMLElement>) => void;
}

interface UseFloatingWindowOptions {
    /** Where the window's place and size are remembered between visits. */
    storageKey: string;
    /** Whether the window is on the screen: it is kept inside the screen only then. */
    shown: boolean;
}

export interface FloatingWindow<T extends HTMLElement> {
    /** On the window itself — the box that moves, is resized and is measured. */
    windowRef: RefObject<T | null>;
    /** Where the window is drawn; null while it sits in the corner it opens in. */
    position: WindowPosition | null;
    /** How large the window is drawn; null while it keeps the size its own classes give it. */
    size: WindowSize | null;
    /** The window's place and size, for its `style`; nothing while it keeps its defaults. */
    style: CSSProperties | undefined;
    /** Whether a drag is under way, for the cursor. */
    dragging: boolean;
    /** Whether a resize is under way. */
    resizing: boolean;
    /**
     * Spread on the title bar: a primary press on it drags the window, except
     * on an element marked `data-window-control` (its minimise and close
     * buttons), which keeps its click.
     */
    dragHandleProps: PointerHandleProps;
    /** The arrow keys move the window, for its move control: a drag is not the only way. */
    onMoveKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
    /** Spread on the handle along one edge or at one corner: a primary press on it resizes the window from there. */
    resizeHandleProps: (edge: ResizeEdge) => PointerHandleProps;
    /**
     * The arrow keys resize the window, for its resize control, as its
     * bottom-right corner would: right and down make it larger.
     */
    onResizeKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/**
 * A window that floats over the page and can be moved and resized: dragged by
 * its title bar and resized by any edge or corner, with a mouse, a pen or a
 * finger, or moved and resized with the arrow keys from its move and resize
 * controls.
 *
 * It opens in the corner its own classes give it, at the size they give it,
 * until the operator moves or resizes it, and from then on where they put it
 * and as large as they made it — remembered in this browser, since a window
 * that has to be moved out of the way again on every visit has not really been
 * moved. Wherever it is, it is kept whole on the screen: a place or a size
 * remembered from a larger screen, a browser window made narrower, or a window
 * grown by what was added to it is drawn as near to that place and size as
 * fits — never changing what was chosen, so the window goes back to it once
 * there is room again, and a laptop plugged back into its monitor opens it
 * where it was, as large as it was.
 *
 * A drag or a resize writes the box straight onto the element and commits it
 * when the pointer lets go: re-rendering whatever the window holds on every
 * pointer move is work neither needs.
 */
export function useFloatingWindow<T extends HTMLElement = HTMLDivElement>({
    storageKey,
    shown,
}: UseFloatingWindowOptions): FloatingWindow<T> {
    const windowRef = useRef<T | null>(null);
    // Where and how large the window is drawn: what the operator chose, or as
    // near to it as the screen allows.
    const [position, setPosition] = useState<WindowPosition | null>(() => readStoredWindowPosition(storageKey));
    const [size, setSize] = useState<WindowSize | null>(() => readStoredWindowSize(storageKey));
    // What the operator chose. A screen too small for it draws the window
    // nearer and smaller, and the window goes back once there is room again.
    const chosen = useRef(position);
    const chosenSize = useRef(size);
    const [dragging, setDragging] = useState(false);
    const [resizing, setResizing] = useState(false);
    const drag = useRef<DragState | null>(null);
    const resize = useRef<ResizeState | null>(null);
    // Where and how large it is drawn as of the last change rather than the
    // last render: two arrow presses can land before the window draws again,
    // and the second must move on from the first.
    const latest = useRef(position);
    const latestSize = useRef(size);
    useLayoutEffect(() => {
        latest.current = position;
        latestSize.current = size;
    }, [position, size]);

    /** Put the window where, and as large as, the operator chose, and remember it. */
    const place = useCallback((next: WindowPosition, nextSize: WindowSize | null = chosenSize.current) => {
        chosen.current = next;
        chosenSize.current = nextSize;
        latest.current = next;
        latestSize.current = nextSize;
        setPosition(next);
        setSize(nextSize);
        writeStoredWindowPosition(storageKey, next, nextSize);
    }, [storageKey]);

    useLayoutEffect(() => {
        const element = windowRef.current;
        if (!shown || !element) return;
        const keepInside = () => {
            // A drag or a resize keeps itself inside, and commits where it ends.
            const target = chosen.current;
            if (drag.current || resize.current || !target) return;
            const viewport = viewportSize();
            const nextSize = chosenSize.current ? clampWindowSize(chosenSize.current, viewport, minimumSize()) : null;
            const box = nextSize ?? element.getBoundingClientRect();
            const next = clampWindowPosition(target, box, viewport);
            setPosition((current) => (current && current.x === next.x && current.y === next.y ? current : next));
            setSize((current) => (
                current && nextSize && current.width === nextSize.width && current.height === nextSize.height
                    ? current
                    : nextSize
            ));
        };
        keepInside();
        window.addEventListener("resize", keepInside);
        const observer = typeof ResizeObserver === "function" ? new ResizeObserver(keepInside) : null;
        observer?.observe(element);
        return () => {
            window.removeEventListener("resize", keepInside);
            observer?.disconnect();
            // A window taken off the screen mid-drag must not come back still dragging.
            drag.current = null;
            resize.current = null;
            setDragging(false);
            setResizing(false);
        };
    }, [shown]);

    const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
        if (event.button !== 0 || event.isPrimary === false) return;
        if ((event.target as Element | null)?.closest?.("[data-window-control]")) return;
        const element = windowRef.current;
        if (!element) return;
        const rect = element.getBoundingClientRect();
        const origin = { x: rect.left, y: rect.top };
        drag.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            origin,
            size: { width: rect.width, height: rect.height },
            at: origin,
            moved: false,
        };
        // The pointer keeps reaching the title bar wherever it goes, and the
        // press neither selects text nor takes the focus from the text box
        // being typed in.
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.preventDefault();
        setDragging(true);
    }, []);

    const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
        const state = drag.current;
        const element = windowRef.current;
        if (!state || !element || event.pointerId !== state.pointerId) return;
        const dx = event.clientX - state.startX;
        const dy = event.clientY - state.startY;
        if (!state.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        state.moved = true;
        state.at = clampWindowPosition({ x: state.origin.x + dx, y: state.origin.y + dy }, state.size, viewportSize());
        element.style.left = `${state.at.x}px`;
        element.style.top = `${state.at.y}px`;
        element.style.right = "auto";
        element.style.bottom = "auto";
    }, []);

    const endDrag = useCallback((event: PointerEvent<HTMLElement>) => {
        const state = drag.current;
        if (!state || event.pointerId !== state.pointerId) return;
        drag.current = null;
        setDragging(false);
        if (state.moved) place(state.at);
    }, [place]);

    const onMoveKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
        const step = event.shiftKey ? FLOATING_WINDOW_KEY_STEP * 4 : FLOATING_WINDOW_KEY_STEP;
        const moves: Record<string, [number, number]> = {
            ArrowLeft: [-step, 0],
            ArrowRight: [step, 0],
            ArrowUp: [0, -step],
            ArrowDown: [0, step],
        };
        const move = moves[event.key];
        const element = windowRef.current;
        if (!move || !element) return;
        event.preventDefault();
        const rect = element.getBoundingClientRect();
        const from = latest.current ?? { x: rect.left, y: rect.top };
        const box = latestSize.current ?? rect;
        place(clampWindowPosition({ x: from.x + move[0], y: from.y + move[1] }, box, viewportSize()));
    }, [place]);

    const endResize = useCallback((event: PointerEvent<HTMLElement>) => {
        const state = resize.current;
        if (!state || event.pointerId !== state.pointerId) return;
        resize.current = null;
        setResizing(false);
        if (state.moved) {
            place({ x: state.at.x, y: state.at.y }, { width: state.at.width, height: state.at.height });
        }
    }, [place]);

    const resizeHandleProps = useCallback((edge: ResizeEdge): PointerHandleProps => ({
        onPointerDown: (event) => {
            if (event.button !== 0 || event.isPrimary === false) return;
            const element = windowRef.current;
            if (!element) return;
            const rect = element.getBoundingClientRect();
            const origin = { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
            resize.current = {
                pointerId: event.pointerId,
                edge,
                startX: event.clientX,
                startY: event.clientY,
                origin,
                at: origin,
                moved: false,
            };
            event.currentTarget.setPointerCapture?.(event.pointerId);
            event.preventDefault();
            setResizing(true);
        },
        onPointerMove: (event) => {
            const state = resize.current;
            const element = windowRef.current;
            if (!state || !element || event.pointerId !== state.pointerId) return;
            const dx = event.clientX - state.startX;
            const dy = event.clientY - state.startY;
            if (!state.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
            state.moved = true;
            state.at = resizedBox(state.origin, state.edge, dx, dy, viewportSize(), minimumSize());
            element.style.left = `${state.at.x}px`;
            element.style.top = `${state.at.y}px`;
            element.style.width = `${state.at.width}px`;
            element.style.height = `${state.at.height}px`;
            element.style.right = "auto";
            element.style.bottom = "auto";
        },
        onPointerUp: endResize,
        onPointerCancel: endResize,
        onLostPointerCapture: endResize,
    }), [endResize]);

    const onResizeKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
        const step = event.shiftKey ? FLOATING_WINDOW_KEY_STEP * 4 : FLOATING_WINDOW_KEY_STEP;
        const changes: Record<string, [number, number]> = {
            ArrowLeft: [-step, 0],
            ArrowRight: [step, 0],
            ArrowUp: [0, -step],
            ArrowDown: [0, step],
        };
        const change = changes[event.key];
        const element = windowRef.current;
        if (!change || !element) return;
        event.preventDefault();
        const rect = element.getBoundingClientRect();
        const from = latest.current ?? { x: rect.left, y: rect.top };
        const fromSize = latestSize.current ?? { width: rect.width, height: rect.height };
        const viewport = viewportSize();
        const nextSize = clampWindowSize(
            { width: fromSize.width + change[0], height: fromSize.height + change[1] },
            viewport,
            minimumSize(),
        );
        // Grown where there is no room to grow, the window moves rather than
        // spilling off the screen.
        place(clampWindowPosition(from, nextSize, viewport), nextSize);
    }, [place]);

    return {
        windowRef,
        position,
        size,
        style: position || size
            ? {
                ...(position ? { left: position.x, top: position.y } : {}),
                ...(size ? { width: size.width, height: size.height } : {}),
            }
            : undefined,
        dragging,
        resizing,
        dragHandleProps: {
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onLostPointerCapture: endDrag,
        },
        onMoveKeyDown,
        resizeHandleProps,
        onResizeKeyDown,
    };
}
